<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNow } from '@vueuse/core';
import { format, isValid, parseISO } from 'date-fns';

const props = defineProps({
  action: { type: String, default: '' },
  dueDate: { type: String, default: '' },
  nextReturn: { type: Object, default: null },
  compact: { type: Boolean, default: false },
});

const { t } = useI18n();
const now = useNow({ interval: 60_000 });
const usesTasks = computed(() => props.nextReturn?.source === 'tasks');
const actionLabel = computed(() =>
  usesTasks.value ? props.nextReturn.task?.title : props.action
);
const dueValue = computed(() =>
  usesTasks.value ? props.nextReturn.task?.due_at : props.dueDate
);
const deadline = computed(() => {
  const date = parseISO(dueValue.value || '');
  return isValid(date) &&
    (usesTasks.value || format(date, 'yyyy-MM-dd') === dueValue.value)
    ? date
    : null;
});
const status = computed(() => {
  if (!deadline.value) return '';
  const today = format(now.value, 'yyyy-MM-dd');
  const day = format(deadline.value, 'yyyy-MM-dd');
  if (usesTasks.value ? deadline.value < now.value : day < today)
    return 'OVERDUE';
  return day === today ? 'DUE_TODAY' : 'DUE_ON';
});
</script>

<template>
  <div
    v-if="usesTasks || actionLabel || deadline || !dueValue"
    class="flex flex-col gap-1 text-xs break-words"
  >
    <p
      v-if="
        (usesTasks && !nextReturn.task) ||
        (!usesTasks && !actionLabel && !dueValue)
      "
      class="m-0 text-n-slate-11"
    >
      {{ t('KANBAN.TASKS.NO_PENDING_RETURN') }}
    </p>
    <div v-if="actionLabel" class="text-n-slate-11">
      <span :class="compact ? 'sr-only' : 'font-medium'">
        {{ t('KANBAN.SIDEBAR.NEXT_ACTION') }}
      </span>
      <p class="m-0" :class="{ truncate: compact }" :title="actionLabel">
        {{ actionLabel }}
      </p>
      <p
        v-if="usesTasks && nextReturn.task?.assignee"
        class="m-0"
        :class="{ truncate: compact }"
        :title="nextReturn.task.assignee.name"
      >
        {{ nextReturn.task.assignee.name }}
      </p>
    </div>
    <div
      v-if="deadline"
      class="flex flex-wrap items-center gap-1 font-medium"
      :class="status === 'OVERDUE' ? 'text-n-ruby-11' : 'text-n-slate-11'"
      data-test-id="next-action-deadline"
    >
      <span
        class="i-lucide-calendar-clock size-3 shrink-0"
        aria-hidden="true"
      />
      <span>{{ t(`KANBAN.SIDEBAR.${status}`) }}</span>
      <time :datetime="dueValue">{{
        format(deadline, usesTasks ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
      }}</time>
    </div>
  </div>
</template>
